import _ from 'lodash';

const HEX_BASE = 16;
const RGB_MAX = 255;
const HEX_PAIR_SIZE = 2;
const RGB_CHANNELS = 3;
const HUE_SEGMENTS = 6;
const HUE_DEGREES = 360;
const GREEN_HUE_OFFSET = 2;
const BLUE_HUE_OFFSET = 4;

const SOFT_BLEND_FG_RATIO = 0.2;

// Dark-mode label fill: a low-alpha tint of the family's saturated `dot`, layered over whatever
// dark surface the chip sits on (mirrors the LabelCDS / expression-builder dark chip treatment).
// The light `bg`/`fg` pair is built for a white canvas, so it leaks a bright pastel pill on dark.
const DARK_FILL_ALPHA = 0.18;

const hexToRgb = hex => [
  parseInt(hex.slice(1, 1 + HEX_PAIR_SIZE), HEX_BASE),
  parseInt(hex.slice(1 + HEX_PAIR_SIZE, 1 + HEX_PAIR_SIZE * 2), HEX_BASE),
  parseInt(hex.slice(1 + HEX_PAIR_SIZE * 2, 1 + HEX_PAIR_SIZE * RGB_CHANNELS), HEX_BASE),
];

const rgbToHex = rgb =>
  `#${rgb
    .map(channel => Math.round(channel).toString(HEX_BASE).padStart(HEX_PAIR_SIZE, '0'))
    .join('')}`;

const blendHex = (hexA, hexB, ratioB) => {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);

  return rgbToHex(a.map((channel, index) => channel * (1 - ratioB) + b[index] * ratioB));
};

const hexToRgba = (hex, alpha) => {
  const [r, g, b] = hexToRgb(hex);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const SYSTEM_COLORS_BASE = [
  { order: 1, name: 'Azure', bg: '#ddeeff', fg: '#2060c0', dot: '#3a7ee8' },
  { order: 2, name: 'Coral', bg: '#ffddd0', fg: '#bc3510', dot: '#d84828' },
  { order: 3, name: 'Fern', bg: '#dff5d4', fg: '#2e6e22', dot: '#429634' },
  { order: 4, name: 'Plum', bg: '#eee0ff', fg: '#7020b4', dot: '#9030d2' },
  { order: 5, name: 'Amber', bg: '#fff0cc', fg: '#a85800', dot: '#c87010' },
  { order: 6, name: 'Teal', bg: '#d4f5f4', fg: '#127478', dot: '#1a9098' },
  { order: 7, name: 'Rose', bg: '#ffdde8', fg: '#a81850', dot: '#c82868' },
  { order: 8, name: 'Lime', bg: '#e8f7c0', fg: '#487206', dot: '#5c9010' },
  { order: 9, name: 'Violet', bg: '#eae4ff', fg: '#5428d0', dot: '#6c40ea' },
  { order: 10, name: 'Tangerine', bg: '#ffe4cc', fg: '#b43c0c', dot: '#d05018' },
  { order: 11, name: 'Sage', bg: '#ddf4e4', fg: '#1e7048', dot: '#2c8c5c' },
  { order: 12, name: 'Crimson', bg: '#ffdcdc', fg: '#b01414', dot: '#d02424' },
  { order: 13, name: 'Sky', bg: '#d8ecff', fg: '#0c64b8', dot: '#1880d4' },
  { order: 14, name: 'Mauve', bg: '#f4e0f8', fg: '#862090', dot: '#9e2cac' },
  { order: 15, name: 'Honey', bg: '#fff4b8', fg: '#9c5c00', dot: '#b87400' },
  { order: 16, name: 'Mint', bg: '#d4f8ee', fg: '#126c5c', dot: '#1a8874' },
  { order: 17, name: 'Cobalt', bg: '#d8e2ff', fg: '#183ab8', dot: '#264cd6' },
  { order: 18, name: 'Flamingo', bg: '#ffd4e4', fg: '#ac1440', dot: '#c81e58' },
  { order: 19, name: 'Olive', bg: '#eef4c8', fg: '#586010', dot: '#6e7a1e' },
  { order: 20, name: 'Slate', bg: '#dce6f8', fg: '#284490', dot: '#3858b4' },
  { order: 21, name: 'Peach', bg: '#ffe8d4', fg: '#b84818', dot: '#d45e28' },
  { order: 22, name: 'Emerald', bg: '#d4f4dc', fg: '#167840', dot: '#229452' },
  { order: 23, name: 'Indigo', bg: '#e0e4ff', fg: '#2c32b8', dot: '#3a44d4' },
  { order: 24, name: 'Saffron', bg: '#ffeeb8', fg: '#a06800', dot: '#be8000' },
  { order: 25, name: 'Pine', bg: '#d8f0e4', fg: '#145e3c', dot: '#1c7a50' },
  { order: 26, name: 'Magenta', bg: '#fcd8f4', fg: '#a0189c', dot: '#be26b8' },
  { order: 27, name: 'Denim', bg: '#d4e4f8', fg: '#1a4890', dot: '#2260b0' },
  { order: 28, name: 'Watermelon', bg: '#ffd8e0', fg: '#b41038', dot: '#ce1e50' },
  { order: 29, name: 'Avocado', bg: '#e4f0c4', fg: '#4c6810', dot: '#60841a' },
  { order: 30, name: 'Grape', bg: '#ecdcff', fg: '#6c18c0', dot: '#8428dc' },
  { order: 31, name: 'Caramel', bg: '#ffeacc', fg: '#a86010', dot: '#c47820' },
  { order: 32, name: 'Lagoon', bg: '#d0f4f8', fg: '#0e6874', dot: '#188490' },
  { order: 33, name: 'Punch', bg: '#ffd8d4', fg: '#b82018', dot: '#d03428' },
  { order: 34, name: 'Sprout', bg: '#e4f8d0', fg: '#407818', dot: '#549428' },
  { order: 35, name: 'Lilac', bg: '#ecdeff', fg: '#7030b8', dot: '#8840d4' },
  { order: 36, name: 'Butter', bg: '#fff8b8', fg: '#986400', dot: '#b47c00' },
  { order: 37, name: 'Seafoam', bg: '#ccf4ec', fg: '#107060', dot: '#168c78' },
  { order: 38, name: 'Raspberry', bg: '#ffd4e8', fg: '#b0105c', dot: '#cc1e74' },
  { order: 39, name: 'Chartreuse', bg: '#ecf8c0', fg: '#507400', dot: '#649010' },
  { order: 40, name: 'Periwinkle', bg: '#e4e8ff', fg: '#3840c0', dot: '#4c54dc' },
  { order: 41, name: 'Sienna', bg: '#ffe4d0', fg: '#b44820', dot: '#d06030' },
  { order: 42, name: 'Jade', bg: '#ccf4e4', fg: '#0e6840', dot: '#168450' },
  { order: 43, name: 'Mulberry', bg: '#f8d8f4', fg: '#9c1490', dot: '#b820ac' },
  { order: 44, name: 'Glacier', bg: '#d0eaf8', fg: '#1460a0', dot: '#1c7abc' },
  { order: 45, name: 'Blush', bg: '#ffd8ec', fg: '#b00c60', dot: '#cc1878' },
  { order: 46, name: 'Moss', bg: '#e8f4c4', fg: '#4a6c14', dot: '#5e8820' },
  { order: 47, name: 'Storm', bg: '#dce4f4', fg: '#304888', dot: '#4060a8' },
  { order: 48, name: 'Melon', bg: '#ffe0d8', fg: '#b83c20', dot: '#d45030' },
  { order: 49, name: 'Clover', bg: '#d8f4dc', fg: '#146430', dot: '#1e8040' },
  { order: 50, name: 'Heather', bg: '#ecdcf8', fg: '#7824a4', dot: '#9030c0' },
  { order: 51, name: 'Flax', bg: '#fff4c4', fg: '#9c6800', dot: '#b88000' },
  { order: 52, name: 'Aqua', bg: '#c8f4f4', fg: '#0c6878', dot: '#148494' },
  { order: 53, name: 'Hibiscus', bg: '#ffc8de', fg: '#ac0c58', dot: '#c81870' },
  { order: 54, name: 'Fennel', bg: '#e4f8c8', fg: '#4a7010', dot: '#5e8c1e' },
  { order: 55, name: 'Wisteria', bg: '#e8dcff', fg: '#6820c0', dot: '#8030dc' },
  { order: 56, name: 'Ochre', bg: '#ffecc0', fg: '#a07000', dot: '#be8800' },
  { order: 57, name: 'Cypress', bg: '#cceee4', fg: '#0c5c4c', dot: '#147860' },
  { order: 58, name: 'Cerise', bg: '#ffd0e4', fg: '#b00848', dot: '#cc1460' },
  { order: 59, name: 'Willow', bg: '#e8f4c0', fg: '#4c7010', dot: '#608c1e' },
  { order: 60, name: 'Dusk', bg: '#dce0f8', fg: '#3438b0', dot: '#4248cc' },
];

export const SYSTEM_COLORS = SYSTEM_COLORS_BASE.map(entry => ({
  ...entry,
  soft: blendHex(entry.bg, entry.fg, SOFT_BLEND_FG_RATIO),
  // Dark-mode pair: low-alpha tint fill + the light pastel as the (bright, on-hue) label colour.
  darkBg: hexToRgba(entry.dot, DARK_FILL_ALPHA),
  darkFg: entry.bg,
}));

/**
 * Maps the 9 legacy milestone hex background values to new palette names.
 */
const LEGACY_HEX_TO_NAME = {
  '#D1E9FF': 'Azure',
  '#FEE4E2': 'Coral',
  '#FEEFC6': 'Amber',
  '#D1FADF': 'Fern',
  '#EAECF5': 'Slate',
  '#F4FFB8': 'Lime',
  '#B5F5EC': 'Teal',
  '#EBE9FE': 'Violet',
  '#FCE7F6': 'Flamingo',
};

const colorsByName = _.keyBy(SYSTEM_COLORS, ({ name }) => name.toLowerCase());
const colorsByHex = _.keyBy(SYSTEM_COLORS, ({ bg }) => bg.toLowerCase());

/**
 * Look up a color entry by its palette name (case-insensitive).
 * Returns the color object or undefined.
 */
const getColorByName = name => {
  if (_.isNil(name)) {
    return;
  }

  return colorsByName[name.toLowerCase()];
};

/**
 * Look up a color entry by its bg hex value (case-insensitive).
 * Returns the color object or undefined.
 */
const getColorByHex = hex => {
  if (_.isNil(hex)) {
    return;
  }

  return colorsByHex[hex.toLowerCase()];
};

/**
 * Resolve a color value that may be a palette name, a new-palette hex,
 * or a legacy hex. Always returns the canonical SYSTEM_COLORS entry
 * (with new palette hex values), or undefined when nothing matches.
 */
export const resolveColor = value => {
  if (_.isNil(value)) {
    return;
  }

  const byName = getColorByName(value);

  if (byName) {
    return byName;
  }

  const byHex = getColorByHex(value);

  if (byHex) {
    return byHex;
  }

  const legacyName = LEGACY_HEX_TO_NAME[value] || LEGACY_HEX_TO_NAME[value.toUpperCase()];

  if (legacyName) {
    return getColorByName(legacyName);
  }

  return;
};

/**
 * Resolve a stored color value to its canonical palette name.
 * Returns the matched name or the provided fallback.
 */
export const resolveColorName = (value, fallback) => resolveColor(value)?.name || fallback;

const isHexColor = value => _.isString(value) && /^#[0-9a-fA-F]{3,8}$/.test(value);

/**
 * Resolve the background/foreground pair for a colour-coded label (milestone/tag chips).
 * Light mode returns the canonical light bg + dark fg (or the raw value), byte-identical to the
 * legacy behaviour. Dark mode returns the family's dark tint + bright on-hue label; for a custom
 * legacy hex with no palette entry it tints the raw colour instead of leaking it as a pale fill,
 * and returns null when there is nothing safe to derive so the caller can fall back to a neutral.
 */
export const getLabelColors = (value, isDark) => {
  const resolved = resolveColor(value);

  if (!isDark) {
    return { background: resolved?.bg ?? value, color: resolved?.fg ?? 'black' };
  }

  if (resolved) {
    return { background: resolved.darkBg, color: resolved.darkFg };
  }

  if (isHexColor(value)) {
    return { background: hexToRgba(value, DARK_FILL_ALPHA), color: value };
  }

  return null;
};

const hexToHue = hex => {
  const r = parseInt(hex.slice(1, 1 + HEX_PAIR_SIZE), HEX_BASE) / RGB_MAX;
  const g = parseInt(hex.slice(1 + HEX_PAIR_SIZE, 1 + HEX_PAIR_SIZE * 2), HEX_BASE) / RGB_MAX;
  const b =
    parseInt(hex.slice(1 + HEX_PAIR_SIZE * 2, 1 + HEX_PAIR_SIZE * RGB_CHANNELS), HEX_BASE) /
    RGB_MAX;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  if (max === min) {
    return 0;
  }

  const d = max - min;

  if (max === r) {
    return Math.round((((g - b) / d + (g < b ? HUE_SEGMENTS : 0)) / HUE_SEGMENTS) * HUE_DEGREES);
  }

  if (max === g) {
    return Math.round((((b - r) / d + GREEN_HUE_OFFSET) / HUE_SEGMENTS) * HUE_DEGREES);
  }

  return Math.round((((r - g) / d + BLUE_HUE_OFFSET) / HUE_SEGMENTS) * HUE_DEGREES);
};

export const SYSTEM_COLORS_BY_HUE = [...SYSTEM_COLORS].sort(
  (a, b) => hexToHue(a.fg) - hexToHue(b.fg),
);
